import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';

// ═══════════════════════════════════════════════════════════════
// ADMIN: Document Expiry Dashboard (Batch 16 M1)
// ═══════════════════════════════════════════════════════════════
// Shows drivers with documents that are expired, expiring soon,
// or pending verification. Allows admin to verify document updates.
// ═══════════════════════════════════════════════════════════════

const DOCUMENT_TYPES = {
  driverLicence: 'NSW Driver Licence (incl. PT Code)',
  vehicleRegistration: 'Vehicle Registration',
  ctpInsurance: 'CTP Green Slip',
  comprehensiveInsurance: 'Comprehensive Insurance',
  wwcc: 'Working With Children Check',
};

export default function AdminDocumentExpiry() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | expired | expiring | pending

  const load = async () => {
    setLoading(true);
    const snap = await getDocs(query(
      collection(db, 'users'),
      where('role', '==', 'driver'),
      where('status', '==', 'active')
    ));
    const list = [];
    snap.forEach(d => list.push({ id: d.id, ...d.data() }));
    setDrivers(list);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const daysUntil = (date) => {
    if (!date) return null;
    const d = date.toDate ? date.toDate() : new Date(date);
    return Math.floor((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  };

  const issuesForDriver = (driver) => {
    const issues = [];
    const docs = driver.documents || {};
    for (const [key, label] of Object.entries(DOCUMENT_TYPES)) {
      const d = docs[key];
      if (!d || !d.expiryDate) {
        if (['driverLicence', 'vehicleRegistration', 'ctpInsurance', 'comprehensiveInsurance'].includes(key)) {
          issues.push({ type: 'missing', docType: key, label, days: null, verified: null });
        }
        continue;
      }
      const days = daysUntil(d.expiryDate);
      if (days < 0) {
        issues.push({ type: 'expired', docType: key, label, days, verified: d.verifiedByAdmin });
      } else if (days <= 30) {
        issues.push({ type: 'expiring', docType: key, label, days, verified: d.verifiedByAdmin });
      } else if (d.verifiedByAdmin === false) {
        issues.push({ type: 'pending', docType: key, label, days, verified: false });
      }
    }
    return issues;
  };

  const filteredDrivers = drivers
    .map(d => ({ driver: d, issues: issuesForDriver(d) }))
    .filter(({ issues }) => {
      if (issues.length === 0) return false;
      if (filter === 'all') return true;
      if (filter === 'expired') return issues.some(i => i.type === 'expired');
      if (filter === 'expiring') return issues.some(i => i.type === 'expiring');
      if (filter === 'pending') return issues.some(i => i.type === 'pending');
      return true;
    });

  const handleVerify = async (driverId, docType, approved) => {
    const notes = approved ? null : prompt('Reason for rejection (optional):');
    try {
      const callable = httpsCallable(functions, 'adminVerifyDocument');
      await callable({ driverId, docType, approved, notes });
      await load();
    } catch (e) {
      alert(`Verification failed: ${e.message}`);
    }
  };

  if (loading) return <div className="p-10 text-center">Loading...</div>;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <button onClick={() => navigate(-1)}
        className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0 mb-4">
        ← Back
      </button>

      <h1 className="font-serif text-3xl text-brand-dark mb-4">Document Expiry</h1>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {[
          { key: 'all', label: 'All issues' },
          { key: 'expired', label: 'Expired' },
          { key: 'expiring', label: 'Expiring (<30d)' },
          { key: 'pending', label: 'Pending verification' },
        ].map(t => (
          <button key={t.key} onClick={() => setFilter(t.key)}
            className={`px-3 py-1.5 rounded-full border-[1.5px] cursor-pointer font-semibold text-[0.85rem] ${
              filter === t.key ? 'border-brand-primary bg-brand-primary text-white' : 'border-slate-200 bg-white text-body'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {filteredDrivers.length === 0 ? (
        <div className="p-10 text-center text-muted">
          No driver documents need attention.
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredDrivers.map(({ driver, issues }) => (
            <div key={driver.id} className="p-3.5 rounded-xl bg-white border border-slate-200">
              <div className="flex justify-between items-start mb-2.5">
                <div>
                  <div className="font-bold text-brand-dark">
                    {driver.firstName} {driver.lastName} · {driver.driverRef || driver.id.slice(0, 6)}
                  </div>
                  <div className="text-[0.8rem] text-muted">
                    {driver.email} · {driver.phone}
                  </div>
                </div>
                {driver.documentsBlocked && (
                  <span className="px-2.5 py-1 rounded-full text-[0.73rem] font-bold bg-red-100 text-red-600">BLOCKED</span>
                )}
              </div>
              {issues.map((issue, idx) => {
                const colors = {
                  expired: { bg: '#fee2e2', color: '#dc2626' },
                  expiring: { bg: '#fef3c7', color: '#92400e' },
                  missing: { bg: '#fee2e2', color: '#dc2626' },
                  pending: { bg: '#dbeafe', color: '#1e40af' },
                };
                const c = colors[issue.type];
                return (
                  <div key={idx}
                    className="p-2.5 rounded-lg flex justify-between items-center gap-2"
                    style={{ background: c.bg, marginTop: idx === 0 ? 0 : 6 }}>
                    <div className="flex-1">
                      <span className="font-bold" style={{ color: c.color }}>{issue.label}</span>
                      <span className="ml-2 text-[0.82rem]" style={{ color: c.color }}>
                        {issue.type === 'expired' && `· Expired ${Math.abs(issue.days)}d ago`}
                        {issue.type === 'expiring' && `· Expires in ${issue.days}d`}
                        {issue.type === 'missing' && '· Not on file'}
                        {issue.type === 'pending' && '· Pending verification'}
                      </span>
                    </div>
                    {issue.type === 'pending' && (
                      <div className="flex gap-1.5">
                        <button onClick={() => handleVerify(driver.id, issue.docType, true)}
                          className="px-2.5 py-1 rounded-md border-0 bg-emerald-600 text-white cursor-pointer font-semibold text-[0.78rem]">
                          Approve
                        </button>
                        <button onClick={() => handleVerify(driver.id, issue.docType, false)}
                          className="px-2.5 py-1 rounded-md border-0 bg-red-600 text-white cursor-pointer font-semibold text-[0.78rem]">
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}